// Schlanker fetch-Wrapper gegen die Cloud-API (G1/G2).
//   1) Basis-URL aus VITE_API_BASE_URL (= Cloud, z.B. https://api.mtksolutions.at).
//   2) JWT-Bearer-Header aus dem Token-Store (localStorage).
//   3) Transparenter Refresh-on-401: ein abgelaufener Access-Token wird einmalig
//      via /api/auth/refresh erneuert und der Request wiederholt (single-flight).
//   4) Fehler in eine `ApiError`-Klasse uebersetzen (React-Query-freundlich).
//
// Bewusst KEIN axios — fetch reicht. Keine Cookies mehr (JWT im Header), darum
// auch kein `credentials: "include"`.

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

const TOKEN_STORAGE_KEY = "bmd_cloud_tokens";

export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

export function getTokens(): Tokens | null {
  try {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function setTokens(tokens: Tokens | null): void {
  if (tokens) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } else {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

export function clearTokens(): void {
  setTokens(null);
}

export function hasTokens(): boolean {
  return getTokens() !== null;
}

function buildHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  const tokens = getTokens();
  if (tokens && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${tokens.access_token}`);
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  return headers;
}

async function parseError(response: Response): Promise<ApiError> {
  let detail: unknown = null;
  try {
    detail = await response.json();
  } catch {
    try {
      detail = await response.text();
    } catch {
      detail = null;
    }
  }
  const detailMessage =
    typeof detail === "object" && detail !== null && "detail" in detail
      ? String((detail as { detail: unknown }).detail)
      : response.statusText;
  return new ApiError(
    `${response.status} ${detailMessage}`,
    response.status,
    detail
  );
}

// --- Token-Refresh (single-flight) --------------------------------
// Mehrere parallele 401er sollen NUR EINEN Refresh ausloesen. Dazu ein
// modul-globales Promise, auf das alle warten.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const tokens = getTokens();
  if (!tokens?.refresh_token) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refresh_token: tokens.refresh_token }),
      });
      if (!res.ok) {
        clearTokens();
        return false;
      }
      const fresh = (await res.json()) as Tokens;
      setTokens({
        access_token: fresh.access_token,
        refresh_token: fresh.refresh_token,
      });
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// Endpunkte, fuer die KEIN Refresh-Retry versucht wird (sonst Endlos-Schleife).
function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/api/auth/login") || path.startsWith("/api/auth/refresh");
}

// Zentrale Request-Huelle: alle Helfer unten gehen hier durch.
async function request(
  path: string,
  init: RequestInit,
  parseJson: boolean,
  allowRefresh = true
): Promise<unknown> {
  const doFetch = () =>
    fetch(`${BASE_URL}${path}`, { ...init, headers: buildHeaders(init.headers) });

  let response = await doFetch();

  if (
    response.status === 401 &&
    allowRefresh &&
    !isAuthEndpoint(path) &&
    (await refreshTokens())
  ) {
    response = await doFetch(); // einmalig mit frischem Token wiederholen
  }

  if (!response.ok) {
    throw await parseError(response);
  }
  return parseJson ? await response.json() : undefined;
}

export async function apiGet<T>(path: string): Promise<T> {
  return (await request(path, { method: "GET" }, true)) as T;
}

export async function apiPostJson<T>(path: string, body: unknown): Promise<T> {
  return (await request(
    path,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    true
  )) as T;
}

export async function apiPatchJson<T>(path: string, body: unknown): Promise<T> {
  return (await request(
    path,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    true
  )) as T;
}

export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  // Bei FormData NIE Content-Type setzen — der Browser baut den boundary-Header.
  return (await request(path, { method: "POST", body: form }, true)) as T;
}

export async function apiDelete(path: string): Promise<void> {
  // 204 No Content → kein JSON parsen.
  await request(path, { method: "DELETE" }, false);
}
